import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Types ───

/** Catalog of system print templates available in v1 (PRD.184 §6.1). */
export type PrescriptionPrintTemplateId =
  | 'classic_clinical'
  | 'clean_classic'
  | 'compact_modern'
  | 'doctor_letterhead'
  | 'minimal_prescription'
  // PRD.284 — Letterhead Classic (traditional pre-printed stationery).
  | 'classic_letterhead';

/**
 * Which configured clinic logo to render on the printout. The actual image
 * URLs are env-provided on the client (VITE_ORG_LOGO_URL / VITE_ORG_LOGO_HORIZONTAL_URL),
 * so the saved setting only stores the choice.
 */
export type PrescriptionPrintLogoSource = 'standard' | 'horizontal' | 'none';

/** PowerSync sqlite row shape — booleans are stored as 0/1 integers. */
export interface PrescriptionPrintSettingsRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  template_id: string | null;
  logo_source: string | null;
  clinic_name: string | null;
  clinic_address: string | null;
  clinic_phone: string | null;
  clinic_email: string | null;
  clinic_website: string | null;
  footer_text: string | null;
  show_patient_demographics: number | null;
  show_complaint: number | null;
  show_vitals: number | null;
  show_item_details: number | null;
  show_instructions: number | null;
  show_doctor_footer: number | null;
  show_signature_block: number | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** UI-friendly settings shape used by the wizard. */
export interface PrescriptionPrintSettingsUI {
  id: string;
  templateId: PrescriptionPrintTemplateId;

  logoSource: PrescriptionPrintLogoSource;
  clinicName: string;
  clinicAddress: string;
  clinicPhone: string;
  clinicEmail: string;
  clinicWebsite: string;
  footerText: string;

  showPatientDemographics: boolean;
  showComplaint: boolean;
  showVitals: boolean;
  showItemDetails: boolean;
  showInstructions: boolean;
  showDoctorFooter: boolean;
  showSignatureBlock: boolean;
}

export const PRESCRIPTION_PRINT_DEFAULTS: Omit<PrescriptionPrintSettingsUI, 'id'> = {
  templateId: 'classic_clinical',
  logoSource: 'standard',
  clinicName: '',
  clinicAddress: '',
  clinicPhone: '',
  clinicEmail: '',
  clinicWebsite: '',
  footerText: '',
  showPatientDemographics: true,
  showComplaint: false,
  showVitals: false,
  showItemDetails: true,
  showInstructions: true,
  showDoctorFooter: true,
  showSignatureBlock: true,
};

// ─── Queries ───

/** Fetch the org-default Prescription Print Settings row (clinic_id IS NULL). */
export function qPrescriptionPrintSettings(): QuerySpec<[]> {
  return {
    key: ['prescription-print-settings'] as const,
    sql: `SELECT * FROM prescription_print_settings WHERE clinic_id IS NULL ORDER BY updated_at DESC LIMIT 1`,
    params: [],
    watch: ['prescription_print_settings'],
  };
}

// ─── Mappers ───

function asBool(v: number | null, fallback: boolean): boolean {
  if (v == null) return fallback;
  return v === 1;
}

export function mapPrescriptionPrintSettingsToUI(
  row: PrescriptionPrintSettingsRow,
): PrescriptionPrintSettingsUI {
  const d = PRESCRIPTION_PRINT_DEFAULTS;
  return {
    id: row.id,
    templateId: (row.template_id ?? d.templateId) as PrescriptionPrintTemplateId,

    logoSource: (row.logo_source ?? d.logoSource) as PrescriptionPrintLogoSource,
    clinicName: row.clinic_name ?? '',
    clinicAddress: row.clinic_address ?? '',
    clinicPhone: row.clinic_phone ?? '',
    clinicEmail: row.clinic_email ?? '',
    clinicWebsite: row.clinic_website ?? '',
    footerText: row.footer_text ?? '',

    showPatientDemographics: asBool(row.show_patient_demographics, d.showPatientDemographics),
    showComplaint: asBool(row.show_complaint, d.showComplaint),
    showVitals: asBool(row.show_vitals, d.showVitals),
    showItemDetails: asBool(row.show_item_details, d.showItemDetails),
    showInstructions: asBool(row.show_instructions, d.showInstructions),
    showDoctorFooter: asBool(row.show_doctor_footer, d.showDoctorFooter),
    showSignatureBlock: asBool(row.show_signature_block, d.showSignatureBlock),
  };
}

export function mapUIToPrescriptionPrintSettingsColumns(
  ui: Omit<PrescriptionPrintSettingsUI, 'id'>,
) {
  const b = (v: boolean) => (v ? 1 : 0);
  return {
    template_id: ui.templateId,
    logo_source: ui.logoSource,
    clinic_name: ui.clinicName || null,
    clinic_address: ui.clinicAddress || null,
    clinic_phone: ui.clinicPhone || null,
    clinic_email: ui.clinicEmail || null,
    clinic_website: ui.clinicWebsite || null,
    footer_text: ui.footerText || null,

    show_patient_demographics: b(ui.showPatientDemographics),
    show_complaint: b(ui.showComplaint),
    show_vitals: b(ui.showVitals),
    show_item_details: b(ui.showItemDetails),
    show_instructions: b(ui.showInstructions),
    show_doctor_footer: b(ui.showDoctorFooter),
    show_signature_block: b(ui.showSignatureBlock),

    updated_at: new Date().toISOString(),
  };
}
